import { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { selectIsSmallScreen } from '../../domain/store/ui/selectors';
import ICON_SETTINGS from '../../shared/assets/menu/icon_settings.png';
import ICON_REMOVE from '../../shared/assets/menu/icon_remove.png';
import ICON_PLUS from '../../shared/assets/menu/icon_plus.png';
import CreateIcon from '@mui/icons-material/Create';
import { Jail } from '../../domain/entities/Jail';
import { useNavigate, useParams } from 'react-router-dom';
import moduleService from '../../application/services/ModuleService';
import http from '../../infrastructure/api/http';
import JailForm from '../components/JailForm';

const RootAdminCenterModuleJails = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [jailToEdit, setJailToEdit] = useState<null | Jail>(null);
  const navigate = useNavigate();
  const [jails, setJails] = useState<Jail[]>([]);
  const isSmallScreen = useSelector(selectIsSmallScreen);
  const [showForm, setShowForm] = useState(false);
  let { moduleId, centerId } = useParams();

  const loadData = async () => {
    try {
      setIsLoading(true);
      const jails = await moduleService.loadAllJailsByModuleId(
        `${centerId}`,
        `${moduleId}`
      );
      setJails(jails);
    } catch (error) {
      alert(`Error al cargar las jaulas`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (centerId && moduleId) {
      loadData();
    }
  }, [centerId, moduleId]);

  return (
    <Box component="div">
      <Grid container spacing={2}>
        <Grid item xs={9} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="h5"
            fontSize={isSmallScreen ? '20px' : undefined}
          >
            Administrar Jaulas
          </Typography>
        </Grid>
        <Grid
          item
          xs={3}
          display="flex"
          justifyContent="flex-end"
          alignContent="end"
        >
          <IconButton
            color="primary"
            aria-label="add"
            onClick={() => {
              //navigate(`/admin/customers/add`);
              setShowForm(true);
            }}
          >
            <img src={ICON_PLUS} alt="Nuevo cliente" width={48} />
          </IconButton>
        </Grid>
        <Grid item xs={12}>
          <TableContainer>
            <Table
              sx={{
                backgroundColor: 'transparent',
                border: 0,
                width: isSmallScreen ? '100%' : '50%',
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell style={{ borderBottomColor: 'transparent' }}>
                    <Typography fontWeight={'600'} align="center">
                      Módulo
                    </Typography>
                  </TableCell>
                  <TableCell style={{ borderBottomColor: 'transparent' }}>
                    <Typography fontWeight={'600'} align="center">
                      Jaula
                    </Typography>
                  </TableCell>
                  <TableCell style={{ borderBottomColor: 'transparent' }}>
                    <Typography fontWeight={'600'} align="center">
                      Acción
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow
                    sx={{
                      'td, th': { border: 0 },
                    }}
                  >
                    <TableCell colSpan={4}>
                      <Box
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          padding: 15,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {jails.map((jail) => (
                  <TableRow key={jail.id}>
                    <TableCell
                      style={{ borderBottomColor: 'transparent' }}
                      align="center"
                    >
                      {jail.module?.name}
                    </TableCell>
                    <TableCell
                      style={{ borderBottomColor: 'transparent' }}
                      align="center"
                    >
                      {jail.name}
                    </TableCell>
                    <TableCell
                      style={{ borderBottomColor: 'transparent' }}
                      align="center"
                      sx={{ padding: 1 }}
                    >
                      <IconButton
                        aria-label="show"
                        onClick={() => {
                          setJailToEdit(jail);
                          setShowForm(true);
                        }}
                      >
                        <CreateIcon style={{ fontSize: '14px' }} />
                      </IconButton>
                      <IconButton
                        aria-label="show"
                        onClick={() => {
                          navigate(`${jail.id}/lamps`);
                        }}
                      >
                        <img
                          src={ICON_SETTINGS}
                          alt="Configuración"
                          width={14}
                        />
                      </IconButton>
                      <IconButton
                        aria-label="show"
                        onClick={async () => {
                          if (
                            window.confirm(
                              '¿Está seguro que desea eliminar este módulo?'
                            )
                          ) {
                            try {
                              await http.delete(
                                `/centers/${centerId}/modules/${jail.module?.id}/jails/${jail.id}`
                              );
                              loadData();
                            } catch (error) {
                              alert(
                                'Se ha producido un error al remover el módulo'
                              );
                            }
                          }
                        }}
                      >
                        <img src={ICON_REMOVE} alt="Eliminar" width={14} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      <Modal
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setJailToEdit(null);
        }}
      >
        <Box
          sx={{
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
          }}
        >
          {centerId && (
            <JailForm
              centerId={parseInt(centerId)}
              jail={jailToEdit}
              moduleId={parseInt(moduleId!)}
              onCreated={() => {
                if (centerId) loadData();
                setShowForm(false);
                setJailToEdit(null);
              }}
              onCancel={() => {
                setShowForm(false);
                setJailToEdit(null);
              }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default RootAdminCenterModuleJails;
